import React, { useCallback, useMemo, useState } from 'react';

export const DATE_PRESET_CUSTOM = 'custom';
export const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this-week', label: 'This week' },
  { id: 'last-week', label: 'Last week' },
  { id: 'this-month', label: 'This month' },
  { id: 'last-month', label: 'Last month' },
  { id: 'this-year', label: 'This year' },
];
export const DEFAULT_DATE_PRESET = 'this-week';

const startOfDay = (date) => new Date(
  date.getFullYear(),
  date.getMonth(),
  date.getDate(),
  0,
  0,
  0,
  0
);

const endOfDay = (date) => new Date(
  date.getFullYear(),
  date.getMonth(),
  date.getDate(),
  23,
  59,
  59,
  999
);

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const startOfWeek = (date) => {
  const current = startOfDay(date);
  const day = current.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(current, diff);
};

const endOfWeek = (date) => endOfDay(addDays(startOfWeek(date), 6));
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
const startOfYear = (date) => new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0);

export const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateInputValue = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

export const getPresetRange = (presetId, now = new Date()) => {
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);

  switch (presetId) {
    case 'today':
      return { start: todayStart, end: todayEnd };
    case 'yesterday': {
      const yesterday = addDays(todayStart, -1);
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    }
    case 'last-week': {
      const currentWeekStart = startOfWeek(now);
      const previousWeekStart = addDays(currentWeekStart, -7);
      return { start: previousWeekStart, end: endOfWeek(previousWeekStart) };
    }
    case 'this-month':
      return { start: startOfMonth(now), end: todayEnd };
    case 'last-month': {
      const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { start: startOfMonth(previousMonth), end: endOfMonth(previousMonth) };
    }
    case 'this-year':
      return { start: startOfYear(now), end: todayEnd };
    case 'this-week':
    default:
      return { start: startOfWeek(now), end: todayEnd };
  }
};

export const resolveDateRange = ({ startDateInput, endDateInput }) => {
  const start = parseDateInputValue(startDateInput);
  const end = parseDateInputValue(endDateInput);
  if (!start || !end) return null;

  return {
    start: startOfDay(start),
    end: endOfDay(end),
  };
};

export const validateDateRange = ({ startDateInput, endDateInput, resolvedDateRange }) => {
  if (!startDateInput || !endDateInput) {
    return 'Select both a start and end date.';
  }
  if (!resolvedDateRange) {
    return 'Enter a valid date range.';
  }
  if (resolvedDateRange.start > resolvedDateRange.end) {
    return 'Start date must be on or before end date.';
  }
  return '';
};

export const useDateRangePickerState = (initialPreset = DEFAULT_DATE_PRESET) => {
  const [selectedPreset, setSelectedPreset] = useState(initialPreset);
  const [startDateInput, setStartDateInput] = useState(() => (
    formatDateInputValue(getPresetRange(initialPreset).start)
  ));
  const [endDateInput, setEndDateInput] = useState(() => (
    formatDateInputValue(getPresetRange(initialPreset).end)
  ));

  const resolvedDateRange = useMemo(
    () => resolveDateRange({ startDateInput, endDateInput }),
    [endDateInput, startDateInput]
  );
  const error = useMemo(
    () => validateDateRange({ startDateInput, endDateInput, resolvedDateRange }),
    [endDateInput, resolvedDateRange, startDateInput]
  );

  const applyPreset = useCallback((presetId) => {
    const { start, end } = getPresetRange(presetId);
    setSelectedPreset(presetId);
    setStartDateInput(formatDateInputValue(start));
    setEndDateInput(formatDateInputValue(end));
  }, []);

  const handleStartDateChange = useCallback((event) => {
    setSelectedPreset(DATE_PRESET_CUSTOM);
    setStartDateInput(event.target.value);
  }, []);

  const handleEndDateChange = useCallback((event) => {
    setSelectedPreset(DATE_PRESET_CUSTOM);
    setEndDateInput(event.target.value);
  }, []);

  return {
    selectedPreset,
    startDateInput,
    endDateInput,
    resolvedDateRange,
    error,
    applyPreset,
    handleStartDateChange,
    handleEndDateChange,
  };
};

const DateRangePicker = ({
  disabled = false,
  error = '',
  hint = '',
  onEndDateChange,
  onPresetChange,
  onStartDateChange,
  presets = DATE_PRESETS,
  selectedPreset,
  startDateInput,
  endDateInput,
}) => (
  <div className="date-range-picker">
    <div className="date-range-picker__label">Date range</div>
    <div className="date-range-picker__presets" role="list" aria-label="Date range presets">
      {presets.map((preset) => (
        <button
          key={preset.id}
          type="button"
          className={`date-range-picker__preset${selectedPreset === preset.id ? ' is-active' : ''}`}
          onClick={() => onPresetChange(preset.id)}
          disabled={disabled}
        >
          {preset.label}
        </button>
      ))}
    </div>
    <div className="date-range-picker__inputs">
      <input
        type="date"
        value={startDateInput}
        onChange={onStartDateChange}
        disabled={disabled}
      />
      <span className="date-range-picker__separator">to</span>
      <input
        type="date"
        value={endDateInput}
        onChange={onEndDateChange}
        disabled={disabled}
      />
    </div>
    <div className={`date-range-picker__hint${error ? ' is-error' : ''}`}>
      {error || hint}
    </div>
  </div>
);

export default DateRangePicker;
